import { createRouter, createWebHistory } from 'vue-router'
import { reportWarning } from '../utils/reporting'
import HomeView from '../views/HomeView.vue'
import RequestView from '../views/RequestView.vue'
import GdprView from '../views/GdprView.vue'
import HelpCenterFallback from '../views/HelpCenterFallback.vue'
import CategoryDetails from '../views/CategoryDetails.vue'
import Section from '../views/Section.vue'
import Article from '../views/Article.vue'
import SearchResult from '../views/SearchResult.vue'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      component: HomeView,
    },
    {
      path: '/request', 
      name: 'request', 
      component: RequestView
    },
    {
      path: '/gdpr', 
      name: 'gdpr',
      component: GdprView
    },
    {
      path: '/helpcenter', 
      name: 'helpcenter', 
      component: HelpCenterFallback
    },
    {
      path: '/category/:categoryId',
      name: 'CategoryDetails',
      component: CategoryDetails
    },
    {
      path: '/category/:categoryId/section/:sectionId',
      name: 'Section',
      component: Section
    },
    {
      path: '/category/:categoryId/section/:sectionId/article/:articleId',
      name: 'Article',
      component: Article
    },
    {
      path: '/search',
      name: 'SearchResult',
      component: SearchResult,
    },
    {
      path: '/:pathMatch(.*)',
      redirect: '/'
    }
  ],
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  }
})

function persistQueryParams(to, from) {
  const queryToPersist = from.query
  const newQuery = { ...queryToPersist, ...to.query }

  if (JSON.stringify(newQuery) !== JSON.stringify(to.query)) {
    return { ...to, query: newQuery }
  }
  return null
}

function checkMissingParams(to) {
  const requiredParams = ['uid', 'iid', 'game', 'platform', 'token']
  const missingParams = requiredParams.filter(
    param => !to.query[param] || to.query[param] === 'undefined' ||
      to.query[param] === 'null' || to.query[param].trim() === ''
  )

  return missingParams
}

function isValidPersistedQuery(obj) {
  if (obj === null || typeof obj !== 'object' || Array.isArray(obj)) return false
  return Object.values(obj).every(v => typeof v === 'string')
}

function parsePersistedQuery(raw) {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw)
    if (!isValidPersistedQuery(parsed)) {
      throw new Error('unexpected shape')
    }
    return parsed
  } catch (err) {
    reportWarning('supportQuery: removing corrupted persisted state', {
      reason: err.message,
      rawValue: raw?.slice(0, 500)
    })
    localStorage.removeItem('supportQuery')
    return null
  }
}

router.beforeEach((to, from, next) => {
  if (to.path === '/helpcenter') {
    if (Object.keys(to.query).length === 0) {
      const savedQuery = parsePersistedQuery(localStorage.getItem('supportQuery'))
      if (savedQuery && Object.keys(savedQuery).length > 0) {
        return next({ path: to.path, query: savedQuery, replace: true })
      }
    }
    return next()
  }

  const savedQuery = parsePersistedQuery(localStorage.getItem('supportQuery'))
  if (savedQuery) {
    const mergedQuery = { ...savedQuery, ...to.query }

    if (checkMissingParams({ query: mergedQuery }).length === 0 &&
      JSON.stringify(mergedQuery) !== JSON.stringify(to.query)) {
      return next({ ...to, query: mergedQuery, replace: true })
    }
  }

  const updatedRoute = persistQueryParams(to, from)
  if (updatedRoute) { return next(updatedRoute) }

  const missingParams = checkMissingParams(to)
  if (missingParams.length > 0) { return next('/helpcenter') }

  next()
})


router.afterEach((to) => {
  if (Object.keys(to.query).length > 0) {
    localStorage.setItem('supportQuery', JSON.stringify(to.query))
  }
})

export default router